<template>
  <ChartDonut
    :class="[
      $style.chart,
      {[$style.chartHalfSize]: halfSizeChart}
    ]"
    :labels="labels"
    :legendPosition="legendPosition"
    :options="options"
    :series="series"
    :showLegend="showLegend"
    @selectDataPoint="handleSelectDataPoint"
  />
</template>

<script>
import { ChartDonut } from '@jumpcloud/ui-components/charts';
import OsManagementMixin from 'client/OsManagement/mixins/OsManagementMixin';

export default {
  name: 'OsManagementDonutChart',

  components: {
    ChartDonut,
  },

  mixins: [OsManagementMixin],

  props: {
    activeTabIndex: {
      type: Number,
      default: 0,
    },
    chartData: {
      type: Array,
      required: true,
    },
    halfSizeChart: {
      type: Boolean,
      default: false,
    },
    legendPosition: {
      type: String,
      default: 'right',
    },
    showLegend: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    isActivityTabSelected() {
      return this.activeTabIndex === 0;
    },

    labels() {
      let labels = [];

      if (this.isActivityTabSelected) {
        labels = this.chartData.map(datum => this.getDonutChartLabel(datum));
      } else if (this.selectedOsData && this.selectedOsData.osVersionList) {
        labels = this.selectedOsData.osVersionList.map(datum => {
          const { version } = this.formatVersionName(this.selectedOsData.name,
            datum.name, false);
          const percentage = (datum.sum * 100) / this.selectedOsData.sum;
          return `${version}: ${datum.sum} (${percentage < 1 ? '< 1%' : `${Math.round(percentage)}%`})`;
        });
      }

      return labels;
    },

    options() {
      return {
        tooltip: {
          enabled: true,
          custom: ({
            series, seriesIndex, w,
          }) => `<div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
   <span class="apexcharts-tooltip-marker" style="background-color: ${w.globals.colors[seriesIndex]};"></span>
   <div class="apexcharts-tooltip-text" style="font-family: inherit; font-size: 12px;">
      <div class="apexcharts-tooltip-y-group"><span class="apexcharts-tooltip-text-y-label">${this.tooltipDescription(seriesIndex)} <br/> ${this.tooltipDeviceCount(series, seriesIndex)}</span></div>
   </div>
</div>`,
        },
      };
    },

    selectedOsData() {
      const index = this.activeTabIndex - 1;
      return this.chartData[index];
    },

    series() {
      let series = [];

      if (this.isActivityTabSelected) {
        series = this.chartData.map(datum => parseInt(datum.sum, 10));
      } else if (this.selectedOsData && this.selectedOsData.osVersionList) {
        series = this.selectedOsData.osVersionList.map(datum => parseInt(datum.sum, 10));
      }

      return series;
    },
  },

  methods: {
    getDonutChartLabel(os) {
      const totalCount = this.chartData.map(item => item.sum).reduce((a, b) => a + b, 0);
      const osName = os.name.split(' ');
      const percentage = (os.sum * 100) / totalCount;
      return `${osName[0]}: ${osName[1].replace(/\(|\)/g, '')} (${percentage < 1 ? '< 1%' : `${Math.round(percentage)}%`})`;
    },

    handleSelectDataPoint(selectedDataPoint) {
      this.$emit('selectDataPoint', selectedDataPoint);
    },

    tooltipDescription(index) {
      let result;
      if (this.isActivityTabSelected) {
        [result] = this.chartData[index].name.split(' ');
      } else {
        const formatted = this.formatVersionName(
          this.selectedOsData.name,
          this.selectedOsData.osVersionList[index].name,
        );

        result = `${formatted.name} ${formatted.version}`;
      }

      return result;
    },

    tooltipDeviceCount(series, index) {
      let count;
      if (this.isActivityTabSelected) {
        count = this.chartData[index].sum;
      } else {
        count = this.selectedOsData.osVersionList[index].sum;
      }
      return `${count} ${count > 1 ? 'Devices' : 'Device'} (${this.getPercentage(series, index)})`;
    },

  },
};
</script>

<style module>
.chart {
  --chart-width: 500px;

  flex: 0 1 auto;
}

.chartHalfSize {
  --chart-width: 405px;
}
</style>
